import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import React, { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { ApplicationSettings } from 'src/modules/applications/types';
import defaultLogo from 'src/ui/assets/bhi_logo.png';
import { isHomePath } from 'src/helpers/errorRecovery';

type ErrorFallbackProps = {
  settings?: ApplicationSettings | null;
  onReload: () => void;
  onRecover: () => void;
  recoveryActionLabel?: string;
  showRecoveryAction?: boolean;
  fullScreen?: boolean;
  showLogo?: boolean;
};

export const reportRenderError = (
  error: unknown,
  context: string,
  componentStack?: string | null,
) => {
  const normalizedError = error instanceof Error ? error : new Error(String(error));

  console.error('[ApplicationErrorBoundary] Unhandled render error', {
    context,
    message: normalizedError.message,
    stack: normalizedError.stack,
    componentStack,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    error: normalizedError,
  });
};

export const ErrorFallback = ({
  settings,
  onReload,
  onRecover,
  recoveryActionLabel = 'Return to home',
  showRecoveryAction,
  fullScreen = true,
  showLogo = true,
}: ErrorFallbackProps) => {
  const applicationName = settings?.name || 'AgencyText';
  const brandColor = settings?.primaryButtonColor || '#254597';
  const brandHoverColor = settings?.primaryButtonHoverColor || '#193069';
  const shouldShowRecoveryAction = showRecoveryAction ?? !isHomePath(window.location.pathname);

  return (
    <Box
      role="alert"
      sx={{
        alignItems: 'center',
        backgroundColor: 'background.default',
        display: 'flex',
        justifyContent: 'center',
        minHeight: fullScreen ? '100vh' : 'calc(100vh - 140px)',
        padding: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          borderTop: `4px solid ${brandColor}`,
          maxWidth: 520,
          padding: { xs: 3, sm: 5 },
          textAlign: 'center',
          width: '100%',
        }}
      >
        {showLogo && (
          <Box
            alt={`${applicationName} logo`}
            component="img"
            src={settings?.logo || defaultLogo}
            sx={{ maxHeight: 64, maxWidth: 220, objectFit: 'contain', mb: 3 }}
          />
        )}
        <Typography component="h1" variant="h4" gutterBottom>
          Something went wrong
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          {applicationName} hit an unexpected problem.{' '}
          {shouldShowRecoveryAction
            ? 'You can reload the page or use the recovery action below.'
            : 'Please reload the page to try again.'}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          {shouldShowRecoveryAction && (
            <Button
              onClick={onRecover}
              startIcon={<HomeIcon />}
              variant="contained"
              sx={{
                backgroundColor: brandColor,
                '&:hover': { backgroundColor: brandHoverColor },
              }}
            >
              {recoveryActionLabel}
            </Button>
          )}
          <Button
            onClick={onReload}
            startIcon={<RefreshIcon />}
            variant="outlined"
            sx={{
              borderColor: brandColor,
              color: brandColor,
              '&:hover': {
                borderColor: brandHoverColor,
                color: brandHoverColor,
              },
            }}
          >
            Reload page
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

type ApplicationErrorBoundaryProps = PropsWithChildren<{
  settings?: ApplicationSettings | null;
  context?: string;
  onReload?: () => void;
  onRecover?: () => void;
  recoveryActionLabel?: string;
  showRecoveryAction?: boolean;
  fullScreen?: boolean;
  showLogo?: boolean;
  resetKey?: string;
  fallback?: ReactNode;
}>;

type ApplicationErrorBoundaryState = {
  hasError: boolean;
};

export class ApplicationErrorBoundary extends React.Component<
  ApplicationErrorBoundaryProps,
  ApplicationErrorBoundaryState
> {
  state: ApplicationErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ApplicationErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportRenderError(error, this.props.context || 'application', errorInfo.componentStack);
  }

  componentDidUpdate(previousProps: ApplicationErrorBoundaryProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          settings={this.props.settings}
          onReload={this.props.onReload || (() => window.location.reload())}
          onRecover={this.props.onRecover || (() => window.location.assign('/home'))}
          recoveryActionLabel={this.props.recoveryActionLabel}
          showRecoveryAction={this.props.showRecoveryAction}
          fullScreen={this.props.fullScreen}
          showLogo={this.props.showLogo}
        />
      );
    }

    return this.props.children;
  }
}

export default ApplicationErrorBoundary;
